import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { Upload as UploadIcon, FileText, CheckCircle, XCircle, Loader2 } from 'lucide-react'

export default function Upload() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.uploadFile(file),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['uploads'] })
      queryClient.invalidateQueries({ queryKey: ['snapshots'] })
      queryClient.invalidateQueries({ queryKey: ['snapshots-latest'] })
      queryClient.invalidateQueries({ queryKey: ['metrics'] })
      queryClient.invalidateQueries({ queryKey: ['aggregate'] })
      queryClient.invalidateQueries({ queryKey: ['portfolio'] })
      queryClient.invalidateQueries({ queryKey: ['accounts'] })

      // Navigate to portfolio view after successful upload
      setTimeout(() => {
        navigate(`/portfolio/${data.id}`)
      }, 1500)
    },
  })

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      setUploadedFile(file)
      uploadMutation.mutate(file)
    }
  }, [uploadMutation])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
    disabled: uploadMutation.isPending,
  })

  const resetUpload = () => {
    setUploadedFile(null)
    uploadMutation.reset()
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Upload Statement</h1>

      <div className="card">
        {!uploadedFile ? (
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input {...getInputProps()} />
            <UploadIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            {isDragActive ? (
              <p className="text-lg text-primary-600">Drop the file here...</p>
            ) : (
              <>
                <p className="text-lg text-gray-700 mb-2">
                  Drag and drop your CSV file here
                </p>
                <p className="text-sm text-gray-500">or click to browse</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* File info */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <FileText className="h-10 w-10 text-gray-400" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{uploadedFile.name}</p>
                <p className="text-sm text-gray-500">
                  {(uploadedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center justify-center gap-3 py-4">
              {uploadMutation.isPending && (
                <>
                  <Loader2 className="h-6 w-6 text-primary-600 animate-spin" />
                  <span className="text-gray-600">Processing file...</span>
                </>
              )}

              {uploadMutation.isSuccess && (
                <>
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  <span className="text-green-600">Upload successful! Redirecting...</span>
                </>
              )}

              {uploadMutation.isError && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <XCircle className="h-6 w-6 text-red-500" />
                    <span className="text-red-600">Upload failed</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-4 max-w-md">
                    {(() => {
                      const error = uploadMutation.error as { response?: { data?: { detail?: string } }; message?: string }
                      return error?.response?.data?.detail || error?.message || 'An error occurred'
                    })()}
                  </p>
                  <button onClick={resetUpload} className="btn btn-secondary">
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-6 pt-6 border-t">
          <h3 className="font-medium text-gray-900 mb-3">File Requirements</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-primary-500">1.</span>
              Export your Interactive Brokers statement as CSV
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500">2.</span>
              Include Net Asset Value, Cash Report, and Open Positions sections
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500">3.</span>
              Remove any sensitive client data and rename the file to end with <code className="bg-gray-100 px-1 rounded">_cleaned.csv</code>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
